import { useQuery } from '@tanstack/react-query'
import { Menu, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import { LogoDaParoquia } from '@/components/LogoDaParoquia'
import { SeletorDeEscala, SeletorDeTema } from '@/components/SeletorDeTema'
import { apiGet } from '@/lib/api'
import { cn } from '@/lib/utils'

export interface ParishSettings {
  name: string
  shortName: string
  description: string
  address: string
  city: string
  state: string
  phone: string
  whatsapp: string
  email: string
  officeHours: string
  instagramUrl: string
  facebookUrl: string
  youtubeUrl: string
  logoUrl: string | null
  logoAlt: string | null
}

const NAV_ITEMS = [
  { to: '/', label: 'Início' },
  { to: '/noticias', label: 'Notícias' },
  { to: '/horarios', label: 'Horários' },
  { to: '/eventos', label: 'Eventos' },
  { to: '/contato', label: 'Contato' },
] as const

/** Chave da preferência de tamanho de fonte, por navegador. */
export function useParishSettings() {
  return useQuery({
    queryKey: ['portal', 'configuracoes'],
    queryFn: () => apiGet<ParishSettings>('/api/portal/configuracoes'),
    staleTime: 10 * 60 * 1000,
  })
}

export function PublicLayout() {
  const { data: parish } = useParishSettings()
  const [menuAberto, setMenuAberto] = useState(false)
  const location = useLocation()

  // Fecha o menu ao navegar; no celular ele ficaria sobre o conteúdo novo.
  useEffect(() => setMenuAberto(false), [location.pathname])

  return (
    <div className="flex min-h-screen flex-col">
      <a href="#conteudo" className="pular-para-conteudo">
        Pular para o conteúdo
      </a>

      <header className="nao-imprimir sticky top-0 z-40 border-b border-borda bg-cartao/95 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-3">
          <Link to="/" className="flex items-center gap-3">
            <LogoDaParoquia
              url={parish?.logoUrl}
              alt={parish?.logoAlt}
              className="size-9 rounded"
            />
            <span className="font-serif text-lg leading-tight font-semibold">
              {parish?.shortName ?? 'Paróquia de Pacaembu'}
            </span>
          </Link>

          <nav aria-label="Navegação principal" className="hidden md:block">
            <ul className="flex items-center gap-1">
              {NAV_ITEMS.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === '/'}
                    className={({ isActive }) =>
                      cn(
                        'block rounded-md px-3 py-2 text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-primario-suave text-primario'
                          : 'hover:bg-afundado hover:text-primario',
                      )
                    }
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          <button
            type="button"
            onClick={() => setMenuAberto((aberto) => !aberto)}
            aria-expanded={menuAberto}
            aria-controls="menu-mobile"
            aria-label={menuAberto ? 'Fechar menu' : 'Abrir menu'}
            className="rounded-md p-2 hover:bg-primario-suave md:hidden"
          >
            {menuAberto ? <X className="size-6" /> : <Menu className="size-6" />}
          </button>
        </div>

        {menuAberto && (
          <nav
            id="menu-mobile"
            aria-label="Navegação principal"
            className="border-t border-borda md:hidden"
          >
            <ul className="mx-auto max-w-5xl px-4 py-2">
              {NAV_ITEMS.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === '/'}
                    className={({ isActive }) =>
                      cn(
                        'block rounded-md px-3 py-3 font-medium',
                        isActive ? 'bg-primario-suave text-primario' : 'hover:bg-afundado',
                      )
                    }
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </header>

      <main id="conteudo" className="flex-1">
        <Outlet />
      </main>

      <Footer parish={parish} />
    </div>
  )
}

function Footer({ parish }: { parish: ParishSettings | undefined }) {
  return (
    <footer className="nao-imprimir mt-16 border-t border-borda bg-faixa text-sobre-faixa">
      <div className="mx-auto grid max-w-5xl gap-8 px-4 py-10 sm:grid-cols-2 lg:grid-cols-3">
        <div>
          <h2 className="font-serif text-lg font-semibold">
            {parish?.name ?? 'Paróquia de Pacaembu'}
          </h2>
          {parish?.address && <p className="mt-2 text-sm text-sobre-faixa">{parish.address}</p>}
          {parish?.city && (
            <p className="text-sm text-sobre-faixa">
              {parish.city} — {parish.state}
            </p>
          )}
        </div>

        <div>
          <h2 className="font-serif text-lg font-semibold">Secretaria</h2>
          <ul className="mt-2 space-y-1 text-sm text-sobre-faixa">
            {parish?.officeHours && <li>{parish.officeHours}</li>}
            {parish?.phone && (
              <li>
                <a href={`tel:${parish.phone.replace(/\D/g, '')}`} className="underline">
                  {parish.phone}
                </a>
              </li>
            )}
            {parish?.email && (
              <li>
                <a href={`mailto:${parish.email}`} className="underline">
                  {parish.email}
                </a>
              </li>
            )}
          </ul>
        </div>

        <div className="space-y-5">
          <div>
            <h2 className="font-serif text-lg font-semibold">Tamanho do texto</h2>
            <div className="mt-2">
              <SeletorDeEscala />
            </div>
          </div>

          <div>
            <h2 className="font-serif text-lg font-semibold">Cores</h2>
            <div className="mt-2">
              <SeletorDeTema />
            </div>
          </div>
          <p className="text-sm text-sobre-faixa">
            <Link to="/privacidade" className="underline">
              Política de privacidade
            </Link>
          </p>
        </div>
      </div>
    </footer>
  )
}
